import React, { memo, Suspense, useCallback, useMemo, useState, useEffect } from "react";
import { Avatar, Button, Skeleton, Typography, Segmented } from "antd/lib";
import dynamic from "next/dynamic";
import { formatThousands } from "@/helpers";
import { ShareAltOutlined, LineChartOutlined } from "@ant-design/icons";
import { TIMELINES_MARKET } from "@/constant";
import type { IPriceChange, TokenData } from "@/models/token";
import { usePrice } from "@/context/PriceContext";
import { useShareModalContext } from "@/context/ShareContext";
import { useTranslations } from "next-intl";
import { SmallChart } from "@/app/components/SmallChart";
import { useRouter } from "next/navigation";
import { useCurrencyContext } from "@/context/CurrencyContext";
import { useLanguageContext } from "@/context/LanguageContext";
import AdsBanner from "../AdsBanner";
import { TradingViewChart } from "../TradingViewChart";
import CandleStickIcon from "@/svg/CandleStickIcon";
import LinkToKlardaApp from "../LinkToKlardaApp";

// Lazy loading for components not needed immediately
const TokenPrice = dynamic(() => import("@/app/components/TokenPrice"), { suspense: true });
const PriceChange = dynamic(() => import("@/app/components/PriceChange"), { suspense: true });
const MyCard = dynamic(() => import("@/app/components/MyCard"), { suspense: true });

const ProductChart = ({
  selectedToken,
  isHideAds = false,
  isLoading = false,
  height = 300,
  chart = "line"
}: {
  selectedToken?: TokenData;
  isHideAds?: boolean;
  isLoading?: boolean;
  height?: number;
  chart?: string;
}) => {
  const [duration, switchDuration] = useState<string>("1D");
  const [durationFormat, switchDurationFormat] = useState<string>("HH:mm");
  const [chartType, setChartType] = useState<string>(chart);
  const { showModal, ShareModalWrapper, isOpenShare } = useShareModalContext();
  const { isTokenLoading } = usePrice();
  const { language } = useLanguageContext();
  const { currentCurrency } = useCurrencyContext();
  const t = useTranslations("home");
  const router = useRouter();
  const percentChange = useMemo(() => {
    const changeKey = `change_${duration}` as keyof IPriceChange;
    return selectedToken?.price_change?.[changeKey]?.percentage ?? 0;
  }, [selectedToken, duration]);

  // Memorizing the volume and market cap formatting
  const formattedVolume = useMemo(
    () => formatThousands(selectedToken?.volume ?? 0, currentCurrency),
    [currentCurrency, selectedToken?.volume],
  );
  const formattedMarketCap = useMemo(
    () => formatThousands(selectedToken?.market_cap ?? 0, currentCurrency),
    [currentCurrency, selectedToken?.market_cap],
  );

  const handleClickToken = useCallback(() => {
    router.push(`/${language}/markets/${selectedToken?.symbol}`);
  }, [language, router, selectedToken?.symbol]);

  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 768);

  const isMobile = useMemo(() => windowWidth <= 768, [windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {(isTokenLoading || isLoading) && <Skeleton active paragraph={{ rows: 10 }} className="mt-2" />}
      {selectedToken && (
        <div className="flex flex-col gap-4">
          {/* Header section */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            {/* Left side */}
            <div className="flex flex-col xl:flex-row xl:items-center gap-4">
              {/* Row 1 (mobile) / Left section (desktop) */}
              <div className="flex items-center justify-between xl:justify-start xl:gap-4">
                <div className="flex items-center gap-4">
                  {/* Token info */}
                  <div className="flex items-center gap-2 cursor-pointer" onClick={handleClickToken}>
                    <Avatar size={24} src={selectedToken.logo_uri} />
                    <Typography.Text strong>{selectedToken.name}</Typography.Text>
                    <Typography.Text type="secondary" className="hidden sm:block">
                      {selectedToken.symbol}
                    </Typography.Text>
                  </div>

                  {/* Price */}
                  <div className="flex items-center gap-2">
                    <Suspense fallback={<Skeleton.Input style={{ width: 100 }} active />}>
                      <TokenPrice symbol={selectedToken.symbol} initPrice={selectedToken.price} />
                    </Suspense>
                    <Suspense fallback={<Skeleton.Input style={{ width: 50 }} active />}>
                      <PriceChange price={percentChange} />
                    </Suspense>
                  </div>
                </div>

                {/* Chart type toggle (mobile only) */}
                <div className="xl:hidden">
                  <Segmented
                    options={[
                      { value: "line", icon: <LineChartOutlined /> },
                      { value: "trading", icon: <CandleStickIcon className="w-4 h-4" /> },
                    ]}
                    value={chartType}
                    onChange={value => setChartType(value.toString())}
                  />
                </div>
              </div>

              {/* Row 2 (mobile) / Stats (desktop) */}
              <div className="flex items-center justify-between xl:justify-start gap-3 text-sm">
                <div className="flex items-center gap-3">
                  <Typography.Text type="secondary">24h Vol: {formattedVolume}</Typography.Text>
                  <Typography.Text type="secondary">•</Typography.Text>
                  <Typography.Text type="secondary">MCap: {formattedMarketCap}</Typography.Text>
                </div>

                {/* Actions (mobile only) */}
                <div className="flex items-center gap-2 xl:hidden">
                  {chartType !== "trading" ? (
                    <Button icon={<ShareAltOutlined />} onClick={() => showModal()} />
                  ) : (
                    <LinkToKlardaApp
                      url={`/${language}/markets/${selectedToken.symbol}/full-chart`}
                      label={t("Advance_Chart")}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right side (desktop only) */}
            <div className="hidden xl:flex items-center gap-4">
              <Segmented
                options={[
                  { value: "line", icon: <LineChartOutlined /> },
                  { value: "trading", icon: <CandleStickIcon className="w-4 h-4" /> },
                ]}
                value={chartType}
                onChange={value => setChartType(value.toString())}
              />

              <div className="flex items-center gap-2">
                {chartType !== "trading" ? (
                  <Button icon={<ShareAltOutlined />} onClick={() => showModal()} />
                ) : (
                  <LinkToKlardaApp
                    url={`/${language}/markets/${selectedToken.symbol}/full-chart`}
                    label={t("Advance_Chart")}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Chart */}
          <ShareModalWrapper>
            {chartType === "trading" ? (
              <TradingViewChart symbol={selectedToken.symbol} height={isMobile ? 300 : height} />
            ) : (
              <SmallChart
                height={isMobile ? 300 : height}
                isOpenShare={isOpenShare}
                duration={duration}
                durationFormat={durationFormat}
                symbol={selectedToken.symbol}
                change={percentChange}
                price={selectedToken.price}
              />
            )}
          </ShareModalWrapper>

          {/* Timeline buttons */}
          <MyCard className="w-full" styles={{ body: { padding: 0, background: "#0000000a", borderRadius: 8 } }}>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:flex rounded-lg">
              {TIMELINES_MARKET.map(d => {
                const changeKey = `change_${d.label}` as keyof IPriceChange;
                const percent = selectedToken?.price_change?.[changeKey]?.percentage ?? 0;
                return (
                  <Button
                    key={d.label}
                    type="text"
                    disabled={d.disabled}
                    className="h-[40px] sm:h-[50px] border-0 flex-1"
                    onClick={() => {
                      switchDuration(d.label);
                      switchDurationFormat(d.format);
                    }}
                    style={{
                      background: duration === d.label ? "#d9f7be" : "transparent",
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs sm:text-sm font-medium">{d.label}</span>
                      {percent && <PriceChange price={percent} className="text-xs sm:text-sm" />}
                    </div>
                  </Button>
                );
              })}
            </div>
          </MyCard>

          {/* Ads */}
          {!isHideAds && (
            <div className="w-[80%] mx-auto">
              <AdsBanner isSmallSize isOnlyAds />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default memo(ProductChart);
